import { Stack } from '@mui/material';
import { AdvancedSearchContext } from 'Context/MedicalReferralsContext/AdvancedSearchProvider';
import { useContext, useEffect, useMemo, useState } from 'react';
import { IReferralsState } from 'reducer/MedicalReferrals/ReferralReducer';
import { FilteredReferences } from './FilteredReferences';
import { useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { FilterTags } from './FilterTags';
import { REFERRAL_STATUS } from 'Enum/medicalReferrals';
import { isAfter, isBefore, isToday } from 'date-fns';
import { IMedicalReferral } from 'API/medicalReferrals';
import { ReferralAccordion } from '../ReferralAccordion';
import { PaginationResult2 } from 'Components';
import { maxMedicalReferralsPerPage } from '..';

interface IReferralList {
  referrals: IReferralsState;
  dispatchReferrals: Function;
  textSearchValue: string;
  loadAllReferrals: Function;
  referralsCount: number;
}

export const ReferralsList = ({
  referrals,
  dispatchReferrals,
  textSearchValue,
  loadAllReferrals,
  referralsCount,
}: IReferralList) => {
  const [validAndOtherReferrals, setValidAndOtherReferrals] = useState<
    IMedicalReferral[]
  >([]);
  const [pagination, setPagination] = useState(true);
  const [activeFilterTags, setActiveFilterTags] = useState<string[]>([]);
  const { advancedSearch } = useContext(AdvancedSearchContext);
  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicalReferrals);

  const getFilterTags = () => {
    const filterTags = [];
    const allReferrals = [
      ...referrals.validReferrals,
      ...referrals.otherReferrals,
    ];
    if (
      allReferrals.find(
        (referral) => referral.statusCode === REFERRAL_STATUS.UNDER_REVIEW
      )
    )
      filterTags.push(dictionary.filterTags.review);

    if (
      allReferrals.find(
        (referral) => referral.statusCode === REFERRAL_STATUS.APPROVED
      )
    )
      filterTags.push(dictionary.filterTags.approved);

    const now = Date.now();
    if (
      allReferrals.find(
        (referral) =>
          (isBefore(new Date(referral.validFromDate), now) ||
            isToday(new Date(referral.validFromDate))) &&
          isAfter(new Date(referral.validToDate), now)
      )
    )
      filterTags.push(dictionary.filterTags.valid);

    if (allReferrals.find((referral) => !referral.isActive))
      filterTags.push(dictionary.filterTags.cancelled);

    if (
      allReferrals.find(
        (referral) =>
          referral.statusCode === REFERRAL_STATUS.REJECTED && referral.isActive
      )
    )
      filterTags.push(dictionary.filterTags.denied);

    if (
      allReferrals.find((referral) =>
        isBefore(new Date(referral.validToDate), now)
      )
    )
      filterTags.push(dictionary.filterTags.invalid);

    if (
      allReferrals.find((referral) =>
        isAfter(new Date(referral.validFromDate), now)
      )
    )
      filterTags.push(dictionary.filterTags.future);

    return filterTags;
  };

  const referralsByFilterTags = useMemo(() => {
    const allReferrals = [
      ...referrals.validReferrals,
      ...referrals.otherReferrals,
    ];

    const now = Date.now();

    return {
      [dictionary.filterTags.review]: allReferrals.filter(
        (referral) =>
          referral.statusCode === REFERRAL_STATUS.UNDER_REVIEW ||
          referral.statusCode === REFERRAL_STATUS.ON_HOLD
      ),

      [dictionary.filterTags.denied]: allReferrals.filter(
        (referral) =>
          referral.statusCode === REFERRAL_STATUS.REJECTED && referral.isActive
      ),

      [dictionary.filterTags.cancelled]: allReferrals.filter(
        (referral) => !referral.isActive
      ),

      [dictionary.filterTags.approved]: allReferrals.filter(
        (referral) => referral.statusCode === REFERRAL_STATUS.APPROVED
      ),

      [dictionary.filterTags.valid]: allReferrals.filter(
        (referral) =>
          (isBefore(new Date(referral.validFromDate), now) ||
            isToday(new Date(referral.validFromDate))) &&
          isAfter(new Date(referral.validToDate), now)
      ),

      [dictionary.filterTags.future]: allReferrals.filter((referral) =>
        isAfter(new Date(referral.validFromDate), now)
      ),

      [dictionary.filterTags.invalid]: allReferrals.filter((referral) =>
        isBefore(new Date(referral.validToDate), now)
      ),
    };
  }, [dictionary, referrals.otherReferrals, referrals.validReferrals]);

  const filteredReferralsByTags = useMemo(() => {
    const referralsByTags = activeFilterTags.flatMap(
      (tag) => referralsByFilterTags[tag]
    );

    let referralRequestNumbers: Record<string, boolean> = {};
    const uniqueAndFilteredReferrals = referralsByTags.filter((referral) => {
      if (!referralRequestNumbers[referral.requestNumber]) {
        referralRequestNumbers[referral.requestNumber] = true;
        return true;
      }
      return false;
    });

    return uniqueAndFilteredReferrals;
  }, [activeFilterTags, referralsByFilterTags]);

  useEffect(() => {
    if (advancedSearch.activeSearch.advanced) {
      setActiveFilterTags([]);
    }
  }, [advancedSearch.activeSearch.advanced]);

  useEffect(() => {
    const allReferrals = [
      ...referrals.validReferrals,
      ...referrals.otherReferrals,
    ];

    if (pagination) {
      setValidAndOtherReferrals(
        allReferrals?.slice(0, maxMedicalReferralsPerPage)
      );
    } else {
      setValidAndOtherReferrals(allReferrals);
    }
  }, [pagination, referrals.otherReferrals, referrals.validReferrals]);

  useEffect(() => {
    setPagination(referralsCount === maxMedicalReferralsPerPage);
  }, [referralsCount]);

  const toggleActiveFilterTag = (tag: string) => {
    setActiveFilterTags([tag]);
    return [tag];
  };

  const resetFilterTags = () => {
    setActiveFilterTags([]);
  };

  if (advancedSearch.activeSearch.advanced)
    return (
      <FilteredReferences
        filteredReferrals={referrals.filteredReferrals}
        dispatchReferrals={dispatchReferrals}
      />
    );

  if (activeFilterTags.length > 0)
    return (
      <Stack gap={'32px'}>
        <FilterTags
          filterTags={getFilterTags()}
          isActive={(tag) => activeFilterTags.indexOf(tag) > -1}
          toggleFilter={toggleActiveFilterTag}
          resetTags={resetFilterTags}
        />
        <Stack gap={'24px'}>
          {filteredReferralsByTags
            .filter((referral) => referral.part.includes(textSearchValue))
            .sort(
              (a, b) =>
                new Date(b.openDate).getTime() - new Date(a.openDate).getTime()
            )
            .map((referral, index) => {
              return <ReferralAccordion referral={referral} key={index} />;
            })}
        </Stack>
      </Stack>
    );

  return (
    <Stack>
      <FilterTags
        filterTags={getFilterTags()}
        isActive={(tag) => activeFilterTags.indexOf(tag) > -1}
        toggleFilter={toggleActiveFilterTag}
        resetTags={resetFilterTags}
      />
      <Stack gap={'24px'}>
        {validAndOtherReferrals.length > 0 &&
          validAndOtherReferrals
            .filter((referral) => referral.part.includes(textSearchValue))
            .sort(
              (a, b) =>
                new Date(b.openDate).getTime() - new Date(a.openDate).getTime()
            )
            .map((referral, index) => {
              return <ReferralAccordion referral={referral} key={index} />;
            })}
        {referralsCount >= maxMedicalReferralsPerPage && (
          <PaginationResult2
            text={pagination ? dictionary.showAll : dictionary.closeList}
            onClick={() => {
              setPagination(!pagination);
              if (referralsCount === maxMedicalReferralsPerPage)
                loadAllReferrals();
            }}
            isPaginated={!pagination}
          />
        )}
      </Stack>
    </Stack>
  );
};
